import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import DEFAULT_AVATAR from "../../constants/Avatar";
import { useAuth } from '../../context/AuthContext';
import { ReusableDataGrid } from "../shared/ReusableDataGrid";

interface Patient {
  id: number | string;
  avatar: string;
  name: string;
  lastAppointment: string;
  time: string;
  contact: string;
  visitType: string;
  reason: string;
  diagnosis: string;
  status: string;
  insurance: string;
  distance: string;
  doctor?: any;
  eventId?: string;
}

interface FilterState {
  dateRange: string;
  timeSlot: string;
  status: string;
  visitType: string;
  visitReason: string;
}

interface PatientAppointmentTableProps {
  from?: "upcoming" | "past";
  currentUser?: string;
  filters?: FilterState;
}


function generateTimeSlots(start: string, end: string, interval: number): string[] {
  const slots: string[] = [];
  
  const parseTime = (timeStr: string) => {
    const parts = timeStr.split(":").map(Number);
    return { hours: parts[0], minutes: parts[1] || 0 };
  };
  
  const startTime = parseTime(start);
  const endTime = parseTime(end);

  let current = new Date();
  current.setHours(startTime.hours, startTime.minutes, 0, 0);

  const endDateTime = new Date();
  endDateTime.setHours(endTime.hours, endTime.minutes, 0, 0);

  while (current < endDateTime) {
    const next = new Date(current.getTime() + interval * 60000);
    
    if (next > endDateTime) break;
    
    const formatSlotTime = (date: Date) => {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    slots.push(`${formatSlotTime(current)} - ${formatSlotTime(next)}`);
    current = next;
  }

  return slots;
}

export default function PatientAppointmentTable({ from, filters }: PatientAppointmentTableProps) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const { user } = useAuth();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelPatient, setCancelPatient] = useState<Patient | null>(null);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [reschedulePatient, setReschedulePatient] = useState<Patient | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState<Dayjs>(dayjs().add(1, "day"));
  const [rescheduleSlot, setRescheduleSlot] = useState<string | null>(null);
  const [showAllRescheduleSlots, setShowAllRescheduleSlots] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const columns: GridColDef[] = [
    // { 
    //   field: "id", 
    //   headerName: "ID #", 
    //   width: 80,
    //   headerClassName: "table-header",
    // },
    {
      field: "name",
      headerName: "Provider Name",
      width: 200,
      headerClassName: "table-header",
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <img
            src={params.row.avatar || DEFAULT_AVATAR}
            alt={params.row.name}
            style={{ 
              width: 32, 
              height: 32, 
              borderRadius: "50%",
              objectFit: "cover",
              border: "1px solid #E2E8F0",
            }}
          />
          <Box sx={{ 
            fontWeight: 500, 
            fontSize: "14px",
            color: "#2D3748",
            fontFamily: "Quicksand"
          }}>
            {params.row.doctor || params.row.name}
          </Box>
        </Box>
      ),
    },
    { 
      field: "lastAppointment", 
      headerName: "Date", 
      width: 120,
      headerClassName: "table-header",
      renderCell: (params) => (
        <Box sx={{ fontSize: "14px", color: "#4A5568", fontFamily: "Quicksand" }}>
          {params.value}
        </Box>
      ),
    },
    { 
      field: "time", 
      headerName: "Time", 
      width: 100,
      headerClassName: "table-header",
      renderCell: (params) => (
        <Box sx={{ fontSize: "14px", color: "#4A5568", fontFamily: "Quicksand" }}>
          {params.value}
        </Box>
      ),
    },
    { 
      field: "visitType", 
      headerName: "Visit Type", 
      width: 150,
      headerClassName: "table-header",
      renderCell: (params) => (
        <Box sx={{ fontSize: "14px", color: "#4A5568", fontFamily: "Quicksand" }}>
          {params.value}
        </Box>
      ),
    },
    { 
      field: "reason", 
      headerName: "Reason", 
      width: 140,
      headerClassName: "table-header",
      renderCell: (params) => (
        <Box sx={{ fontSize: "14px", color: "#4A5568", fontFamily: "Quicksand" }}>
          {params.value}
        </Box>
      ),
    },
    { 
      field: "insurance", 
      headerName: "Insurance", 
      width: 140,
      headerClassName: "table-header",
      renderCell: (params) => (
        <Box sx={{ fontSize: "14px", color: "#4A5568", fontFamily: "Quicksand" }}>
          {params.value}
        </Box>
      ),
    },
    
    ...(from === "upcoming"
      ? [
        {
          field: "action",
          headerName: "Action",
          width: 200,
          sortable: false,
          filterable: false,
          headerClassName: "table-header",
          renderCell: (params: any) => {
            if (params.row.status === "CANCELLED") {
              return (
                <span style={{
                  backgroundColor: '#FED7D7',
                  color: '#E53E3E',
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                }}>
                  Cancelled
                </span>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 1, padding: "8px 0" }}>
                
                <button
                  onClick={() => handleCancel(params.row)}
                  style={{
                    padding: "6px 16px",
                    backgroundColor: "transparent",
                    border: "1px solid #FF4D4F",
                    borderRadius: "6px",
                    color: "#FF4D4F",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 500,
                    fontFamily: "Quicksand",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#FF4D4F";
                    e.currentTarget.style.color = "#ffffff";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#FF4D4F";
                  }}
                >
                  Cancel
                </button>


                <button
                  onClick={() => handleReschedule(params.row)}
                  style={{
                    padding: "6px 16px",
                    backgroundColor: "transparent",
                    border: "1px solid #0ea5e9",
                    borderRadius: "6px",
                    color: "#0ea5e9",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 500,
                    fontFamily: "Quicksand",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#0ea5e9";
                    e.currentTarget.style.color = "#ffffff";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#0ea5e9";
                  }}
                >
                  Reschedule
                </button>
              </Box>
            );
          },
        },
      ]
      : [ { 
      field: "status", 
      headerName: "Status", 
      width: 140,
      headerClassName: "table-header",
      renderCell: (params:any) => {
         if (params.row.status === "CANCELLED") {
              return (
                <span style={{
                  backgroundColor: '#FED7D7',
                  color: '#E53E3E',
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                }}>
                  Cancelled
                </span>
              );
            }

             if (params.row.status === "PENDING") {
              return (
                <span style={{
                  backgroundColor: '#cff5cfff',
                  color: '#00ff1aff',
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                }}>
                Confirmed
                </span>
              );
            }
            
    }}]),
  ];

  const fetchPatients = useCallback(async () => {
    try {
      const token = (user as any)?.token || localStorage.getItem('token') || '';
      
      if (!token) {
        console.error("No token found");
        toast.error("Authentication token not found");
        return;
      }

      const BASE_URL = 'https://vivotal-backend.vercel.app/api/v1';
      let apiUrl = `${BASE_URL}/appointments?limit=100&page=1`;

      if (from === "upcoming") {
        apiUrl += '&filter_type=upcoming';
      } else if (from === "past") {
        apiUrl += '&filter_type=past';
      }

      const axiosResponse = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const appointments = axiosResponse.data?.data || [];

      if (!appointments || appointments.length === 0) {
        setPatients([]);
        return;
      }

      const mapped = appointments.map((apt: any) => {
        const appointmentDate = new Date(apt.appointment_date);
        const formattedDate = `${(appointmentDate.getMonth() + 1).toString().padStart(2, "0")}/${appointmentDate
          .getDate()
          .toString()
          .padStart(2, "0")}/${appointmentDate.getFullYear()}`;

        const firstName = apt.patient?.patient?.first_name || '';
        const lastName = apt.patient?.patient?.last_name || '';
        const patientName = `${firstName} ${lastName}`.trim() || "Unknown Patient";

        const providerFirstName = apt.provider?.provider?.first_name || '';
        const providerLastName = apt.provider?.provider?.last_name || '';
        const providerTitle = apt.provider?.provider?.title || 'Dr.';
        const providerName = `${providerTitle} ${providerFirstName} ${providerLastName}`.trim() || "Unknown Provider";

        const formattedVisitType = apt.visit_type
          ? apt.visit_type.split('_').map((word: string) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')
          : "In Person";

        return {
          id: apt.id,
          avatar: DEFAULT_AVATAR,
          name: patientName,
          lastAppointment: formattedDate,
          time: apt.appointment_time?.substring(0, 5) || "N/A",
          contact: apt.patient?.patient?.contact_number || "N/A",
          visitType: formattedVisitType,
          reason: apt.reason_for_visit || "Not specified",
          diagnosis: apt.diagnosis || "Not provided",
          insurance: apt.insurance_provider || "Not provided",
          distance: "N/A",
          status: apt.status || "PENDING",
          doctor: providerName,
          eventId: apt.id,
          timestamp: new Date(apt.appointment_date),
        } as Patient & { timestamp: Date };
      });

      const sortedAppointments = mapped.sort((a: Patient & { timestamp: Date }, b: Patient & { timestamp: Date }) => 
        (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0)
      );

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const filtered = sortedAppointments.filter((patient: Patient & { timestamp: Date }) => {
        const [mm, dd, yyyy] = patient.lastAppointment.split("/");
        const appointmentDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
        
        let matchesDate = true;
        if (from === "upcoming") {
          matchesDate = appointmentDate >= today;
        } else if (from === "past") {
          matchesDate = appointmentDate < today;
        }

        return matchesDate;
      });

      setPatients(filtered);
    } catch (err) {
      console.error("Error fetching appointments:", err);
      toast.error("Failed to load appointments.");
    }
  }, [from, user]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  useEffect(() => {
    if (!filters) {
      setFilteredPatients(patients);
      return;
    }

    let filtered = [...patients];

    // Filter by date range
    if (filters.dateRange) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter((patient) => {
        const [mm, dd, yyyy] = patient.lastAppointment.split("/");
        const appointmentDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
        appointmentDate.setHours(0, 0, 0, 0);

        switch (filters.dateRange) {
          case "today":
            return appointmentDate.getTime() === today.getTime();
          case "tomorrow":
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            return appointmentDate.getTime() === tomorrow.getTime();
          case "this-week":
            const weekEnd = new Date(today);
            weekEnd.setDate(weekEnd.getDate() + 7);
            return appointmentDate >= today && appointmentDate <= weekEnd;
          case "next-week":
            const nextWeekStart = new Date(today);
            nextWeekStart.setDate(nextWeekStart.getDate() + 7);
            const nextWeekEnd = new Date(today);
            nextWeekEnd.setDate(nextWeekEnd.getDate() + 14);
            return appointmentDate >= nextWeekStart && appointmentDate <= nextWeekEnd;
          default:
            return true;
        }
      });
    }

    // Filter by time slot
    if (filters.timeSlot) {
      filtered = filtered.filter((patient) => {
        const time = patient.time;
        const [hours] = time.split(":").map(Number);

        switch (filters.timeSlot) {
          case "morning":
            return hours >= 6 && hours < 12;
          case "afternoon":
            return hours >= 12 && hours < 18;
          case "evening":
            return hours >= 18 && hours < 24;
          default:
            return true;
        }
      });
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter((patient) => {
        const status = patient.status.toLowerCase();
        switch (filters.status) {
          case "confirmed":
            return status === "pending" || status === "confirmed";
          case "pending":
            return status === "pending";
          case "cancelled":
            return status === "cancelled";
          default:
            return true;
        }
      });
    }

    // Filter by visit type
    if (filters.visitType) {
      filtered = filtered.filter((patient) => {
        const visitType = patient.visitType.toLowerCase();
        switch (filters.visitType) {
          case "in-person":
            return visitType.includes("person") || visitType === "in person";
          case "virtual":
            return visitType.includes("virtual");
          default:
            return true;
        }
      });
    }

    // Filter by visit reason
if (filters.visitReason) {
      filtered = filtered.filter((patient) => {
        const reason = patient.reason.toLowerCase();
        switch (filters.visitReason) {
          case "follow-up":
            return reason.includes("follow");
          case "consultation":
            return reason.includes("consultation");
          case "checkup":
            return reason.includes("checkup") || reason.includes("check-up") || reason.includes("routine");
          case "new-user":
            return reason.includes("new-user") || reason.includes("new");
          default:
            return true;
        }
      });
    }

    setFilteredPatients(filtered);
  }, [patients, filters]);

  const handleCancel = (appointment: Patient) => {
    setCancelPatient(appointment);
    setSelectedCancelReason("");
    setCancelReason("");
    setIsCancelDialogOpen(true);
  };

  const confirmCancellation = async () => {
    if (!cancelPatient || !cancelPatient.eventId) return;

    setIsCancelling(true);
    try {
      const token = (user as any)?.token || localStorage.getItem('token') || '';
      if (!token) {
        toast.error("Authentication token not found");
        setIsCancelling(false);
        return;
      }

      const BASE_URL = 'https://vivotal-backend.vercel.app/api/v1';

      const finalReason = selectedCancelReason === "other" ? cancelReason : selectedCancelReason;

      await axios.put(
        `${BASE_URL}/appointments/${cancelPatient.eventId}`,
        {
          status: "CANCELLED",
          cancellation_reason: finalReason,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // No need to manually create no-show record here
      // Backend automatically creates no-show records when appointment is cancelled
      // (See backend: update.ts - creates 2 records with follow-up messages)

      toast.success(`Cancellation requested for ${cancelPatient.name}`);
      setIsCancelDialogOpen(false);
      setCancelPatient(null);
      setSelectedCancelReason("");
      setCancelReason("");
      fetchPatients();
    } catch (err) {
      console.error("Cancel appointment error:", err);
      toast.error(`Failed to cancel appointment for ${cancelPatient.name}`);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReschedule = (appointment: Patient) => {
    setReschedulePatient(appointment);
    setRescheduleDate(dayjs().add(1, "day"));
    setRescheduleSlot(null);
    setShowAllRescheduleSlots(false);
    setIsRescheduleDialogOpen(true);
  };

  const confirmReschedule = async () => {
    if (!reschedulePatient || !rescheduleSlot) {
      toast.error("Please select a date and time slot.");
      return;
    }

    try {
      const token = (user as any)?.token || localStorage.getItem('token') || '';
      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      const BASE_URL = 'https://vivotal-backend.vercel.app/api/v1';
      const appointmentTime = rescheduleSlot.split(" - ")[0] + ":00";

      await axios.put(
        `${BASE_URL}/appointments/${reschedulePatient.eventId}`,
        {
          appointment_date: rescheduleDate.format("YYYY-MM-DD"),
          appointment_time: appointmentTime,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(`Appointment rescheduled for ${reschedulePatient.name}`);
      setIsRescheduleDialogOpen(false);
      setReschedulePatient(null);
      setRescheduleSlot(null);
      fetchPatients();
    } catch (err) {
      console.error("Reschedule appointment error:", err);
      toast.error(`Failed to reschedule appointment for ${reschedulePatient.name}`);
    }
  };

  return (
    <div style={{ overflowX: "auto", boxSizing: "border-box" }}>
      <style>
        {`
          .table-header {
            font-family: 'Quicksand' !important;
            font-weight: 500 !important;
            font-size: 13px !important;
            color: #A0AEC0 !important;
            text-transform: capitalize !important;
          }
          .MuiDataGrid-root {
            border: none !important;
            font-family: 'Quicksand' !important;
          }
          .MuiDataGrid-cell {
            border-bottom: 1px solid #E2E8F0 !important;
          }
          .MuiDataGrid-columnHeaders {
            border-bottom: 1px solid #E2E8F0 !important;
            background-color: #F7FAFC !important;
          }
          .MuiDataGrid-row:hover {
            background-color: #F7FAFC !important;
          }
        `}
      </style>
      <Box sx={{ 
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        padding: "16px 0"
      }}>
        <Box sx={{ 
          padding: "0 16px 16px",
          fontFamily: "Quicksand",
          fontSize: "14px",
          fontWeight: 500,
          color: "#2D3748"
        }}>
          Appointments : {filteredPatients.length}
        </Box>
        <ReusableDataGrid<Patient>
          columns={columns}
          data={filteredPatients}
          onSelectionChange={(sel, rows) => {
            console.log("Selected row IDs:", sel);
            console.log("Selected row data:", rows);
          }}
        />
      </Box>

      <Dialog open={isRescheduleDialogOpen} onClose={() => setIsRescheduleDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent dividers>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Typography fontWeight={600} mb={2}>
                Select New Appointment Date
              </Typography>
              <DatePicker
                value={rescheduleDate}
                onChange={(newValue) => {
                  if (dayjs.isDayjs(newValue)) {
                    setRescheduleDate(newValue);
                  }
                }}
                minDate={dayjs().add(1, "day")}
                slotProps={{
                  textField: { fullWidth: true, size: "small", sx: { mb: 2 } },
                }}
              />
            </LocalizationProvider>

            {rescheduleSlot && (
              <Typography
                variant="body2"
                fontWeight={600}
                color="primary"
                mb={2}
                textAlign="center"
              >
                Selected: {rescheduleSlot}
              </Typography>
            )}

            <Typography fontWeight={600} mb={1} fontSize="14px">
              Available Time Slots
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {generateTimeSlots("09:00", "17:00", 30)
                .slice(0, showAllRescheduleSlots ? undefined : 6)
                .map((slot, idx) => (
                  <Button
                    key={idx}
                    variant={rescheduleSlot === slot ? "contained" : "outlined"}
                    size="small"
                    fullWidth
                    onClick={() => setRescheduleSlot(slot)}
                    sx={{ fontFamily: "Quicksand" }}
                  >
                    {slot}
                  </Button>
                ))}
              {generateTimeSlots("09:00", "17:00", 30).length > 6 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowAllRescheduleSlots((prev) => !prev)}
                  sx={{ mt: 1 }}
                >
                  {showAllRescheduleSlots ? "Show Less" : "Show More"}
                </Button>
              )}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRescheduleDialogOpen(false)} color="inherit">
            Close
          </Button>
          <Button
            onClick={confirmReschedule}
            variant="contained"
            sx={{ backgroundColor: "#0ea5e9" }}
            disabled={!rescheduleSlot}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isCancelDialogOpen} onClose={() => setIsCancelDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select a reason for cancellation:
          </Typography>
          <RadioGroup
            value={selectedCancelReason}
            onChange={(e) => {
              setSelectedCancelReason(e.target.value);
              if (e.target.value !== "other") {
                setCancelReason("");
              }
            }}
          >
            <FormControlLabel
              value="Schedule conflict / Personal emergency"
              control={<Radio />}
              label="Schedule conflict / Personal emergency"
            />
            <FormControlLabel
              value="Weather conditions"
              control={<Radio />}
              label="Weather conditions"
            />
            <FormControlLabel
              value="Family emergency"
              control={<Radio />}
              label="Family emergency"
            />
            <FormControlLabel
              value="Transportation issues"
              control={<Radio />}
              label="Transportation issues"
            />
            <FormControlLabel
              value="other"
              control={<Radio />}
              label="Other (please specify)"
            />
          </RadioGroup>

          {selectedCancelReason === "other" && (
            <TextField
              label="Please specify your reason"
              multiline
              fullWidth
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              variant="outlined"
              sx={{ mt: 2 }}
              placeholder="Type your reason here..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCancelDialogOpen(false)} color="inherit" disabled={isCancelling}>
            Cancel
          </Button>
          <Button
            onClick={confirmCancellation}
            color="error"
            disabled={
              isCancelling ||
              !selectedCancelReason ||
              (selectedCancelReason === "other" && !cancelReason.trim())
            }
            startIcon={isCancelling ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isCancelling ? "Cancelling..." : "Confirm Cancellation"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
